import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Badge } from "@/components/ui/PageHeader";
import {
  getDaily,
  listPersonalities,
  listArtForms,
  listHandicrafts,
  type DharoharCard,
} from "@/api/dharohar";
import clsx from "clsx";

type Tab = "daily" | "personalities" | "art-forms" | "handicrafts";

export default function DharoharLab() {
  const [tab, setTab] = useState<Tab>("daily");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dharohar (Heritage)"
        subtitle="Daily heritage card + browse personalities, art forms, handicrafts by state."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Dharohar" },
        ]}
      />

      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {(["daily", "personalities", "art-forms", "handicrafts"] as Tab[]).map(
          (t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={clsx(
                "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                tab === t
                  ? "border-primary-600 text-primary-700"
                  : "border-transparent text-gray-500 hover:text-gray-700",
              )}
            >
              {t === "daily"
                ? "Today"
                : t === "personalities"
                  ? "Personalities"
                  : t === "art-forms"
                    ? "Art Forms"
                    : "Handicrafts"}
            </button>
          ),
        )}
      </div>

      {tab === "daily" && <DailyTab />}
      {tab !== "daily" && <ListTab kind={tab} />}
    </div>
  );
}

function DailyTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["dharohar-daily"],
    queryFn: getDaily,
  });
  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {data.data.personality && <CardTile c={data.data.personality} />}
      {data.data.artForm && <CardTile c={data.data.artForm} />}
      {data.data.handicraft && <CardTile c={data.data.handicraft} />}
    </div>
  );
}

function ListTab({ kind }: { kind: "personalities" | "art-forms" | "handicrafts" }) {
  const { data, isLoading } = useQuery({
    queryKey: ["dharohar-list", kind],
    queryFn: () =>
      kind === "personalities"
        ? listPersonalities({ limit: 30 })
        : kind === "art-forms"
          ? listArtForms({ limit: 30 })
          : listHandicrafts({ limit: 30 }),
  });
  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!data || data.data.items.length === 0)
    return <p className="text-sm text-gray-500">No items.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {data.data.items.map((c) => (
        <CardTile key={c.id} c={c} />
      ))}
    </div>
  );
}

function CardTile({ c }: { c: DharoharCard }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      {c.imageUrl ? (
        <img src={c.imageUrl} alt="" className="w-full h-32 object-cover" />
      ) : (
        <div className="w-full h-32 bg-gradient-to-br from-primary-100 to-accent-100" />
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-gray-900">{c.title}</h3>
          <Badge variant="gray">{c.type.replace("_", " ")}</Badge>
        </div>
        {(c.state || c.scope) && (
          <div className="text-xs text-gray-500 mb-2">
            {c.state}
            {c.scope === "roots" && " · from your roots"}
            {c.scope === "all_india" && " · from across India"}
          </div>
        )}
        {c.description && (
          <p className="text-sm text-gray-600 line-clamp-3">{c.description}</p>
        )}
      </div>
    </div>
  );
}
